import { useRef, useState } from 'react';
import { requireLegacy } from '../../legacy/legacyGlobals';
import { takeAuthRedirectError } from './authRedirectError';
import { G_IC, MAIL_IC } from './loginArt';
import { useTurnstileWidget } from './useTurnstileWidget';

const asHtml = (html: string) => ({ __html: html });

const EMAIL_SHAPE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/**
 * The existing-user sign-in screen (legacy screenLogin): Google redirect and
 * Turnstile-protected magic link only — no password is ever collected. The
 * status line swaps the intro copy for progress and error messages exactly
 * like the legacy screen did.
 */
export function LoginScreen() {
  const t = requireLegacy('t');
  const inputRef = useRef<HTMLInputElement>(null);
  const turnstileBoxRef = useRef<HTMLDivElement>(null);
  const turnstileWidget = useTurnstileWidget(turnstileBoxRef);
  const [status, setStatus] = useState<string | null>(() => {
    const authError = takeAuthRedirectError();
    return authError ? 'Sign-in didn’t complete: ' + authError : null;
  });
  const [sending, setSending] = useState(false);

  const say = (message: string) => setStatus(message);

  const signInWithGoogle = () => {
    say('Taking you to Google…');
    requireLegacy('sbGoogleSignIn')({ from: 'login' });
  };

  const sendMagicLink = async () => {
    const input = inputRef.current!;
    const email = input.value.trim();
    if (!EMAIL_SHAPE.test(email)) {
      input.focus();
      input.style.borderColor = '#D9534F';
      say(t('emailInvalid'));
      return;
    }
    const captchaToken = turnstileWidget.getToken();
    if (!captchaToken && window.CAPTCHA_REQUIRED !== false) {
      say(t('captchaWait'));
      return;
    }
    setSending(true);
    say('Sending your link…');
    try {
      requireLegacy('rememberPendingAuth')({ from: 'login' });
      await requireLegacy('sbMagicLink')(email, captchaToken);
      say('Link sent to ' + email + ' — open it on this device and it signs you straight in.');
    } catch (error) {
      say('Couldn’t send the link: ' + (error as Error).message);
    }
    turnstileWidget.reset();
    setSending(false);
  };

  const backToLanding = () => {
    requireLegacy('screenLanding')();
  };

  return (
    <div className="loginwrap">
      <div className="rinner">
        <h3>{t('loginTitle')}</h3>
        <p>{status ?? t('loginBody')}</p>
        <div className="rbtns">
          <button
            className="rbtn"
            onClick={signInWithGoogle}
            dangerouslySetInnerHTML={asHtml(G_IC + '<span>' + t('btnGoogle') + '</span>')}
          />
          <div className="rsep">{t('or')}</div>
          <div className="rmail">
            <input ref={inputRef} type="email" placeholder={t('emailPh')} autoComplete="email" aria-label="Your email address" />
            {/* style set as the legacy cssText strings so even the serialized
                style attributes diff clean against the original build */}
            <button
              ref={(node) => { if (node) node.style.cssText = 'display:flex; align-items:center; justify-content:center; gap:9px;'; }}
              disabled={sending}
              onClick={() => void sendMagicLink()}
              dangerouslySetInnerHTML={asHtml(MAIL_IC + ' ' + t('btnMagic'))}
            />
          </div>
          <div
            ref={(node) => {
              turnstileBoxRef.current = node;
              if (node) node.style.cssText = 'margin-top:10px; display:flex; justify-content:center;';
            }}
            className="turnstile-box"
          />
        </div>
        <button className="rlater" onClick={backToLanding}>{t('back')}</button>
      </div>
    </div>
  );
}
